import React, { useEffect } from 'react';
import {
    Box,
    Tabs,
    Tab,
    Stack,
    CircularProgress,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactPlayer from 'react-player';
import FileDownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useContent } from '../../../../context/ContentContext';
import axiosInstance from '../../../../utils/axiosInstance';
import { saveAs } from 'file-saver';

const FAQComponent = () => {
    const {
        FAQs: faqs,
        categories,
        selectedCategory,
        setSelectedCategory,
        loading,
    } = useContent();

    useEffect(() => {
        // Set the default category to the first one if selectedCategory is null
        if (!selectedCategory && categories.length > 0) {
            setSelectedCategory(categories[0].id);
        }
    }, [categories, selectedCategory, setSelectedCategory]);

    const handleCategoryChange = (event, newValue) => {
        setSelectedCategory(newValue);
    };

    // Helper function to generate the full file URL
    const getFileUrl = (filePath) => {
        if (!filePath) return null;
        return `${axiosInstance.defaults.baseURL}/${filePath}`;
    };

    // Helper function to determine file type (image or video)
    const isImageFile = (filePath) => /\.(jpg|jpeg|png|gif|webp)$/i.test(filePath);
    const isVideoFile = (filePath) => /\.(mp4|webm|ogg)$/i.test(filePath);

    // Copy text to clipboard
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert('FAQ answer copied to clipboard!');
        }).catch((err) => {
            alert('Failed to copy text: ' + err);
        });
    };

    // Download file
    const handleDownload = (filePath) => {
        saveAs(getFileUrl(filePath), filePath.split('/').pop());
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* FAQ Section Wrapped in Card */}
            <Card variant="outlined" sx={{
                minWidth: "350px",
            }}>
                <CardContent>
                    {/* Tabs for Categories */}
                    <Tabs
                        value={selectedCategory || false}
                        onChange={handleCategoryChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="FAQ Categories"
                    >
                        {categories.map((category) => (
                            <Tab key={category.id} label={category.name} value={category.id} />
                        ))}
                    </Tabs>

                    {/* Content: Loading Spinner or FAQ Cards */}
                    {/* {loading ? (
                        <Stack alignItems="center" sx={{ mt: 4 }}>
                            <CircularProgress />
                        </Stack>
                    ) : ( */}
                    <Stack spacing={1} sx={{ mt: 4, padding: 0 }}>
                        {/* FAQ Cards with Accordion */}
                        {faqs && faqs.length > 0 ? (
                            faqs.map((faq) => (
                                <Accordion key={faq.id}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls={`faq-content-${faq.id}`}
                                        id={`faq-header-${faq.id}`}
                                    >
                                        <Typography variant="h6">{faq.question}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="body1">{faq.answer}</Typography>
                                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                            {/* Copy Button */}
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                startIcon={<ContentCopyIcon />}
                                                onClick={() => handleCopy(faq.answer)}
                                            >
                                                Copy
                                            </Button>
                                        </Stack>
                                        {faq.file && (
                                            <Box sx={{ mt: 2 }}>
                                                {isImageFile(faq.file) ? (
                                                    <img
                                                        src={getFileUrl(faq.file)}
                                                        alt="FAQ Attachment"
                                                        style={{ maxWidth: '100%', borderRadius: 4 }}
                                                    />
                                                ) : isVideoFile(faq.file) ? (
                                                    <Box
                                                        sx={{
                                                            position: 'relative',
                                                            paddingTop: '56.25%', // 16:9 Aspect Ratio
                                                            height: 0,
                                                            overflow: 'hidden',
                                                            maxWidth: '100%',
                                                        }}
                                                    >
                                                        <ReactPlayer
                                                            url={getFileUrl(faq.file)}
                                                            controls
                                                            width="100%"
                                                            height="100%"
                                                            style={{ position: 'absolute', top: 0, left: 0 }}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Unsupported file type.
                                                    </Typography>
                                                )}
                                                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                                    {/* Download Button */}
                                                    {faq.file && (
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            fullWidth
                                                            startIcon={<FileDownloadIcon />}
                                                            onClick={() => handleDownload(faq.file)}
                                                        >
                                                            Download
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </Box>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No FAQs available for this category.
                            </Typography>
                        )}
                    </Stack>
                    {/* )} */}
                </CardContent>
            </Card>
        </Box>
    );
};

export default FAQComponent;
